import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import styled from "@emotion/styled";
import axios from "axios";
import moment from "moment";
import { Row, Col, Form, Button, ButtonGroup, ListGroup, Modal, Table, Badge, Spinner } from "react-bootstrap";
import swal from "sweetalert";
import Select from "react-select";
import Loader from "views/universal/Loader/Loader";
import { savOperations } from "state/ducks/store/sav";
import { orderOperations } from "state/ducks/store/order";
import TourMap, { getDepotForTour } from "./TourMap";
import { ROUTING_MODES, DEFAULT_ROUTING_MODE, fetchRoute, pointAlongRoute } from "./routing";
import ActionModal from "./ActionModal";
import NotesModal from "./NotesModal";
import CustomerDetailModal from "./CustomerDetailModal";
import GuideModal from "./GuideModal";
import TutorialOverlay from "./TutorialOverlay";

// Couleurs de secours si la charrette n'a pas de couleur définie
const FALLBACK_COLORS = ["#e6194b", "#3cb44b", "#4363d8", "#f58231", "#911eb4", "#008080", "#9a6324", "#800000"];

// Géocodage : API adresse data.gouv.fr (gratuite, sans clé), biaisée sur Lyon
const GEOCODE_URL = "https://api-adresse.data.gouv.fr/search/";
const LYON_CENTER = { lat: 45.7578, lon: 4.8320 };

// Emoji le plus proche de la couleur de la charrette (pour le message Connecteam)
const COLOR_EMOJIS = [
  { emoji: "🔴", rgb: [230, 30, 40] },
  { emoji: "🟠", rgb: [255, 145, 45] },
  { emoji: "🟡", rgb: [255, 210, 60] },
  { emoji: "🟢", rgb: [60, 175, 75] },
  { emoji: "🔵", rgb: [45, 110, 235] },
  { emoji: "🟣", rgb: [150, 70, 190] },
  { emoji: "🟤", rgb: [145, 100, 60] },
  { emoji: "⚫", rgb: [35, 35, 40] },
  { emoji: "⚪", rgb: [235, 235, 235] },
];

const colorToEmoji = (hex) => {
  if (!hex) {
    return "⚪";
  }
  let value = hex.replace("#", "");
  if (value.length === 3) {
    value = value.split("").map((char) => char + char).join("");
  }
  if (value.length !== 6) {
    return "⚪";
  }
  const r = parseInt(value.substr(0, 2), 16);
  const g = parseInt(value.substr(2, 2), 16);
  const b = parseInt(value.substr(4, 2), 16);

  let best = COLOR_EMOJIS[0];
  let bestDistance = Infinity;
  COLOR_EMOJIS.forEach((color) => {
    const distance = Math.pow(r - color.rgb[0], 2)
      + Math.pow(g - color.rgb[1], 2)
      + Math.pow(b - color.rgb[2], 2);
    if (distance < bestDistance) {
      bestDistance = distance;
      best = color;
    }
  });
  return best.emoji;
};

/**
 * Intendance charrette : carte des tournées B2C du jour.
 * Sélection de tournées, puis de points de retrait, pour déclencher
 * des actions groupées (SMS, email, crédit cagnotte).
 */
const Intendance = (props) => {

  const dispatch = useDispatch();

  const [date, setDate]                       = useState(moment().format("YYYY-MM-DD"));
  const [tours, setTours]                     = useState([]);
  const [isLoading, setIsLoading]             = useState(true);
  const [selectedTourIds, setSelectedTourIds] = useState([]);
  const [selectedSpots, setSelectedSpots]     = useState([]); // [{spot_id, spot_name, tourId, tourName}]
  const [action, setAction]                   = useState(null); // 'sms' | 'email' | 'credit'
  const [establishmentFilter, setEstablishmentFilter] = useState("");
  const [nameFilter, setNameFilter]           = useState("");
  const [addressQuery, setAddressQuery]             = useState("");
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [addressFocused, setAddressFocused]         = useState(false); // ferme les suggestions au blur
  const [addressMarker, setAddressMarker]           = useState(null); // {lat, lng, label}
  const [deliverymen, setDeliverymen]               = useState([]);
  const [notesTarget, setNotesTarget]               = useState(null); // {vehicleId, vehicleName, spotId?, spotName?}
  const [showRecap, setShowRecap]                   = useState(false);
  const [isSendingRecap, setIsSendingRecap]         = useState(false);
  const [recapFeedback, setRecapFeedback]           = useState(null); // {variant, text}
  const [showGuide, setShowGuide]                   = useState(false);
  const [tutorial, setTutorial]                     = useState(null); // {guide, stepIndex}
  const [customerQuery, setCustomerQuery]           = useState("");
  const [selectedCustomer, setSelectedCustomer]     = useState(null);
  const [customerOrders, setCustomerOrders]         = useState([]);
  const [isSearchingOrders, setIsSearchingOrders]   = useState(false);
  const [focusSpot, setFocusSpot]                   = useState(null); // {spotId, lat, lng, ts}
  const [bikeEstimate, setBikeEstimate]             = useState(null); // {spotId, spotName, minutes, km, latlngs, isLoading, error}
  const [smsPrefill, setSmsPrefill]                 = useState(null); // contenu SMS pré-rempli (depuis l'estimation vélo)
  const [detailCustomerId, setDetailCustomerId]     = useState(null); // fiche client complète en modal
  const [showSpotPicker, setShowSpotPicker]         = useState(false); // choix du point cible de l'estimation vélo
  const [spotPickerFilter, setSpotPickerFilter]     = useState("");
  const [staff, setStaff]                           = useState([]); // intendance du jour : [{establishment|null, delivery_man}]
  const [routingMode, setRoutingMode]               = useState(DEFAULT_ROUTING_MODE); // 'bike' | 'voiture' : tracés + estimations
  const [bikeTarget, setBikeTarget]                 = useState(null); // point visé par l'estimation, pour la recalculer au changement de mode
  const [estimatedPosition, setEstimatedPosition]   = useState(null); // {tourId, lat, lng, label, color}

  // Liste complète des clients (chargée par l'init de la page SAV, comme en gestion client)
  const savCustomers = useSelector(
    (state) => state.store.sav.admin.customers,
    shallowEqual
  );

  // Établissement de la session (transmis par le BO à l'authentification)
  const sessionEstablishment = useSelector(
    (state) => state.session.establishment,
    shallowEqual
  );

  // Pré-sélectionne l'établissement de la session dans le filtre, une seule fois
  // (au premier chargement des tournées), sans écraser un choix de l'utilisateur
  const establishmentDefaultApplied = useRef(false);
  useEffect(() => {
    if (establishmentDefaultApplied.current || !sessionEstablishment || tours.length === 0) {
      return;
    }
    establishmentDefaultApplied.current = true;
    // N'applique le défaut que si cet établissement a des tournées ce jour-là,
    // sinon le sélecteur pointerait sur une option inexistante
    if (tours.some((tour) => tour.establishment && String(tour.establishment.id) === String(sessionEstablishment))) {
      setEstablishmentFilter(String(sessionEstablishment));
    }
  }, [sessionEstablishment, tours]);

  const normalize = (str) => (str || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");

  // Mode de déplacement courant : porte le libellé et l'emoji utilisés dans toute
  // l'UI d'itinéraire (bandeau d'estimation, popup de choix du point, marqueur)
  const currentMode = ROUTING_MODES[routingMode] || ROUTING_MODES[DEFAULT_ROUTING_MODE];

  // Charge les tournées à chaque changement de date
  useEffect(() => {
    setIsLoading(true);
    setSelectedSpots([]);
    // La recherche client est liée au jour affiché
    setSelectedCustomer(null);
    setCustomerOrders([]);
    setDetailCustomerId(null);
    setFocusSpot(null);
    setEstimatedPosition(null);
    dispatch(savOperations.getItineraries(date))
      .then((result) => {
        const items = (result?.data?.result || [])
          .filter((tour) => tour.itinerary && tour.itinerary.length > 0)
          .map((tour, index) => ({
            ...tour,
            displayColor: tour.color || FALLBACK_COLORS[index % FALLBACK_COLORS.length],
          }))
          // Ordre alphanumérique naturel (FL 2 avant FL 10), l'API ne trie pas
          .sort((a, b) => (a.name || "").localeCompare(b.name || "", undefined, { numeric: true, sensitivity: "base" }));
        setTours(items);
        // Ne garde que les tournées encore présentes ce jour-là
        setSelectedTourIds((prev) => prev.filter((id) => items.some((tour) => tour.id === id)));
      })
      .catch(() => {
        setTours([]);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [date, dispatch]);

  const selectedTours = tours.filter((tour) => selectedTourIds.includes(tour.id));
  const selectedSpotIds = selectedSpots.map((spot) => spot.spot_id);

  // Tournées du récap livreurs : respecte le filtre par établissement
  const recapTours = establishmentFilter
    ? tours.filter((tour) => tour.establishment && String(tour.establishment.id) === establishmentFilter)
    : tours;
  const recapEstablishmentName = establishmentFilter && recapTours.length > 0 && recapTours[0].establishment
    ? recapTours[0].establishment.name
    : null;

  // Envoie le récap livreurs affiché (avec le filtre établissement) sur Connecteam
  const sendRecapToConnecteam = () => {
    const sorted = [...recapTours].sort((a, b) => {
      const estA = a.establishment ? a.establishment.name : "";
      const estB = b.establishment ? b.establishment.name : "";
      return estA.localeCompare(estB) || (a.name || "").localeCompare(b.name || "");
    });
    const lines = sorted.map((tour) => {
      const deliveryMan = tour.assigned_delivery_man;
      const startAt = tour.start_at ? ` (départ ${moment(tour.start_at).format("HH:mm")})` : "";
      return `${colorToEmoji(tour.displayColor)} ${tour.name}${startAt} : ${deliveryMan
        ? deliveryMan.firstname + (deliveryMan.phone ? ` ${deliveryMan.phone}` : "")
        : "Non affecté"}`;
    });
    // Intendance en tête : la globale (qui couvre tout) puis celles des
    // établissements affichés, pour que les livreurs sachent qui appeler
    const staffLines = staff
      .filter((item) => !establishmentFilter
        || item.establishment === null
        || String(item.establishment.id) === establishmentFilter)
      .sort((a, b) => (a.establishment ? a.establishment.name : "").localeCompare(b.establishment ? b.establishment.name : ""))
      .map((item) => {
        const person = item.delivery_man;
        return `🏠 Intendance ${item.establishment ? item.establishment.name : "(tous établissements)"}`
          + ` : ${person.firstname}${person.phone ? ` ${person.phone}` : ""}`;
      });

    const message =
      `🚴 Répartition Charrette du ${moment(date).format("DD/MM/YYYY")}` +
      `${recapEstablishmentName ? ` — ${recapEstablishmentName}` : ""}\n\n` +
      (staffLines.length > 0 ? staffLines.join("\n") + "\n\n" : "") +
      lines.join("\n");

    swal({
      title: "Envoyer sur Connecteam ?",
      text: `Le récap de ${recapTours.length} tournée(s) sera posté dans la conversation Connecteam de la livraison.`,
      icon: "warning",
      buttons: {
        cancel: "Annuler",
        confirm: "Envoyer",
      },
    }).then((confirm) => {
      if (confirm !== true) {
        return;
      }
      setIsSendingRecap(true);
      setRecapFeedback(null);
      dispatch(savOperations.sendIntendanceRecapConnecteam(message))
        .then((result) => {
          const data = result?.data?.result;
          setRecapFeedback(data && data.success
            ? { variant: "success", text: "Récap envoyé sur Connecteam." }
            : { variant: "danger", text: `Échec de l'envoi : ${(data && data.message) || "erreur inconnue"}` });
        })
        .catch(() => {
          setRecapFeedback({ variant: "danger", text: "Échec de l'envoi sur Connecteam." });
        })
        .finally(() => {
          setIsSendingRecap(false);
        });
    });
  };

  // Tous les points du jour (uniques), sélectionnables aussi depuis la popup d'envoi
  const availableSpots = tours.reduce((acc, tour) => {
    (tour.itinerary || []).forEach((point) => {
      if (!acc.some((spot) => spot.spot_id === point.spot_id)) {
        acc.push({
          spot_id: point.spot_id,
          spot_name: point.spot_name,
          tourId: tour.id,
          tourName: tour.name,
          contactPhone: tour.contact_phone,
        });
      }
    });
    return acc;
  }, []);

  // Points ciblables par l'estimation vélo : uniquement ceux géolocalisés (les
  // autres feraient échouer le calcul). Suit le filtre établissement, comme le
  // récap livreurs ; la popup a sa propre recherche.
  const bikeTargetPoints = recapTours.reduce((acc, tour) => {
    (tour.itinerary || []).forEach((point) => {
      if (point.spot_coordinates && point.spot_coordinates.lat
        && !acc.some((item) => item.point.spot_id === point.spot_id)) {
        acc.push({ point, tour });
      }
    });
    return acc;
  }, []);

  const filteredBikeTargetPoints = spotPickerFilter.trim()
    ? bikeTargetPoints.filter((item) =>
        normalize(item.point.spot_name).includes(normalize(spotPickerFilter))
        || normalize(item.tour.name).includes(normalize(spotPickerFilter))
        || normalize(item.point.spot_number).includes(normalize(spotPickerFilter))
      )
    : bikeTargetPoints;

  const closeSpotPicker = () => {
    setShowSpotPicker(false);
    setSpotPickerFilter("");
  };

  // Suggestions de points de retrait dans la recherche d'adresse : localiser le
  // livreur sur un point du jour (« il vient de quitter X ») sans retaper l'adresse.
  // Affichées au-dessus des suggestions d'adresse.
  const addressSpotSuggestions = (() => {
    const query = addressQuery.trim();
    if (query.length < 2 || (addressMarker && query === addressMarker.label)) {
      return [];
    }
    return bikeTargetPoints.filter((item) =>
      normalize(item.point.spot_name).includes(normalize(query))
      || normalize(item.point.spot_number).includes(normalize(query))
    ).slice(0, 5);
  })();

  // Place le marqueur du livreur sur un point de retrait du jour
  const selectSpotAsAddress = (item) => {
    setAddressMarker({
      lat: item.point.spot_coordinates.lat,
      lng: item.point.spot_coordinates.lng,
      label: `${item.point.spot_name} (${item.tour.name})`,
    });
    setAddressQuery(`${item.point.spot_name} (${item.tour.name})`);
    setAddressSuggestions([]);
    setBikeEstimate(null);
  };

  // Lance l'estimation vélo vers le point choisi, et affiche sa tournée pour
  // que le marqueur turquoise du point cible soit visible sur la carte
  const chooseBikeTarget = (item) => {
    closeSpotPicker();
    if (!selectedTourIds.includes(item.tour.id)) {
      setSelectedTourIds((prev) => [...prev, item.tour.id]);
    }
    estimateTravelTime(item.point);
  };

  // Filtres de la liste des tournées (n'affectent pas les tournées déjà sélectionnées)
  const establishments = tours.reduce((acc, tour) => {
    if (tour.establishment && !acc.some((establishment) => establishment.id === tour.establishment.id)) {
      acc.push(tour.establishment);
    }
    return acc;
  }, []);

  // Établissements affichés dans le récap : suit le filtre, comme recapTours
  const recapEstablishments = establishmentFilter
    ? establishments.filter((establishment) => String(establishment.id) === establishmentFilter)
    : establishments;

  // Le filtre libre cherche dans le nom de la tournée ET dans les noms des points de retrait
  const nameFilterActive = nameFilter.trim().length > 0;
  const matchesNameFilter = (label) => normalize(label).includes(normalize(nameFilter));

  const filteredTours = tours.filter((tour) => {
    if (establishmentFilter && (!tour.establishment || String(tour.establishment.id) !== establishmentFilter)) {
      return false;
    }
    if (nameFilterActive) {
      const tourMatches = matchesNameFilter(tour.name);
      const spotMatches = (tour.itinerary || []).some((point) => matchesNameFilter(point.spot_name));
      if (!tourMatches && !spotMatches) {
        return false;
      }
    }
    return true;
  });

  // Points de retrait correspondant au filtre libre : mis en valeur sans masquer les autres
  const highlightedSpotIds = nameFilterActive
    ? tours.reduce((acc, tour) => {
        (tour.itinerary || []).forEach((point) => {
          if (matchesNameFilter(point.spot_name) && !acc.includes(point.spot_id)) {
            acc.push(point.spot_id);
          }
        });
        return acc;
      }, [])
    : [];

  // Recherche client rapide (nom, prénom, téléphone, email) — filtrage local
  // multi-termes, insensible aux accents, téléphone comparé sur les chiffres seuls
  const customerSuggestions = (() => {
    const query = customerQuery.trim();
    if (query.length < 2 || !savCustomers) {
      return [];
    }
    const terms = normalize(query).split(/\s+/).filter(Boolean);
    return savCustomers.filter((customer) => {
      const phoneDigits = (customer.phone || "").replace(/\D/g, "");
      return terms.every((term) => {
        const termDigits = term.replace(/\D/g, "");
        return (
          normalize(customer.firstname).includes(term) ||
          normalize(customer.lastname).includes(term) ||
          normalize(customer.email).includes(term) ||
          (termDigits.length >= 2 && phoneDigits.includes(termDigits))
        );
      });
    }).slice(0, 8);
  })();

  // Recherche les commandes du jour du client sélectionné
  const selectCustomer = (customer) => {
    setSelectedCustomer(customer);
    setCustomerQuery("");
    setCustomerOrders([]);
    setIsSearchingOrders(true);
    dispatch(orderOperations.getOrders({
      customers: [customer.id],
      startDate: date,
      endDate: date,
      groups: ["order_info", "order_multiple"],
    }))
      .then((result) => {
        setCustomerOrders(result?.data?.result?.orders || []);
      })
      .catch(() => {
        setCustomerOrders([]);
      })
      .finally(() => {
        setIsSearchingOrders(false);
      });
  };

  const clearCustomer = () => {
    setSelectedCustomer(null);
    setCustomerOrders([]);
    setCustomerQuery("");
    setFocusSpot(null);
    setDetailCustomerId(null);
  };

  // Retrouve le point/tournée d'une commande via le code du point (ou le nom de la charrette)
  const findOrderPoint = (order) => {
    const numberSpot = order.delivery_info && order.delivery_info.number_spot;
    if (!numberSpot) {
      return null;
    }
    for (const tour of tours) {
      const point = (tour.itinerary || []).find((item) => item.spot_number === numberSpot);
      if (point) {
        return { tour, point };
      }
    }
    return null;
  };

  // Centre la carte sur le point d'une commande (sélectionne la tournée si besoin)
  // et fait défiler la liste de gauche jusqu'au point
  const locateOrder = (match) => {
    if (!selectedTourIds.includes(match.tour.id)) {
      setSelectedTourIds((prev) => [...prev, match.tour.id]);
    }
    if (match.point.spot_coordinates && match.point.spot_coordinates.lat) {
      setFocusSpot({
        spotId: match.point.spot_id,
        lat: match.point.spot_coordinates.lat,
        lng: match.point.spot_coordinates.lng,
        ts: Date.now(),
      });
    }
    // Laisse le temps à la tournée de se déplier avant de scroller
    setTimeout(() => {
      const row = document.getElementById(`point-row-${match.tour.id}-${match.point.spot_id}`);
      if (row) {
        row.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 200);
  };

  // Ouvre la popup SMS pour le point de l'estimation vélo, message pré-rempli
  // avec le retard estimé et le nouveau créneau (heure d'arrivée estimée)
  const sendSmsForEstimate = () => {
    if (!bikeEstimate || bikeEstimate.isLoading || bikeEstimate.error) {
      return;
    }
    // Retrouve le point et sa tournée pour le sélectionner
    let found = null;
    for (const tour of tours) {
      const point = (tour.itinerary || []).find((item) => item.spot_id === bikeEstimate.spotId);
      if (point) {
        found = { tour, point };
        break;
      }
    }
    if (!found) {
      return;
    }
    // Sélectionne uniquement ce point (cible claire pour l'envoi)
    setSelectedSpots([{
      spot_id: found.point.spot_id,
      spot_name: found.point.spot_name,
      tourId: found.tour.id,
      tourName: found.tour.name,
      contactPhone: found.tour.contact_phone,
    }]);

    const arrival = moment().add(bikeEstimate.minutes, "minutes");
    let delayText = "A RENSEIGNER";
    if (bikeEstimate.deliveryTime && date === moment().format("YYYY-MM-DD")) {
      const target = moment(`${date} ${moment(bikeEstimate.deliveryTime).format("HH:mm")}`, "YYYY-MM-DD HH:mm");
      const diff = target.diff(arrival, "minutes");
      if (diff < 0) {
        delayText = `${-diff} min`;
      }
    }
    setSmsPrefill(
      `Votre livraison a du retard (${delayText}). Toutes nos excuses. `
      + `Nouveau créneau : ${arrival.format("HH:mm")} . Trop tard pour vous ?`
    );
    setAction("sms");
  };

  // Résumé de l'estimation vélo affiché dans l'étiquette du marqueur 🚲
  let addressEstimateText = null;
  if (bikeEstimate && !bikeEstimate.isLoading && !bikeEstimate.error) {
    addressEstimateText = `≈ ${bikeEstimate.minutes} min ${currentMode.travelLabel} jusqu'à ${bikeEstimate.spotName}`;
    if (bikeEstimate.deliveryTime && date === moment().format("YYYY-MM-DD")) {
      const estimateTarget = moment(`${date} ${moment(bikeEstimate.deliveryTime).format("HH:mm")}`, "YYYY-MM-DD HH:mm");
      const estimateArrival = moment().add(bikeEstimate.minutes, "minutes");
      const estimateDiff = estimateTarget.diff(estimateArrival, "minutes");
      addressEstimateText += estimateDiff >= 0
        ? ` — dans les temps (${estimateDiff} min de marge)`
        : ` — en retard de ${-estimateDiff} min`;
    }
  }

  // Place le livreur sur la carte : soit à un point fixe (dépôt, point de retrait),
  // soit le long de son trajet vélo en cours ({from, to, fraction}).
  // Le trajet suit le même itinéraire vélo que celui tracé sur la carte : d'abord
  // une position provisoire en ligne droite, affinée dès que BRouter a répondu
  // (et qui reste la position finale si BRouter est indisponible).
  const showEstimatedPosition = (plan, tour, mode) => {
    const base = { tourId: tour.id, color: tour.displayColor, label: plan.label };

    if (!plan.from) {
      setEstimatedPosition({ ...base, lat: plan.lat, lng: plan.lng });
      return;
    }

    const straight = {
      lat: plan.from[0] + plan.fraction * (plan.to[0] - plan.from[0]),
      lng: plan.from[1] + plan.fraction * (plan.to[1] - plan.from[1]),
    };
    setEstimatedPosition({ ...base, ...straight });

    fetchRoute([plan.from, plan.to], mode).then((route) => {
      const onRoute = route ? pointAlongRoute(route.latlngs, plan.fraction) : null;
      if (onRoute) {
        setEstimatedPosition({ ...base, ...onRoute });
      }
    });
  };

  // Estime où le livreur devrait être sur sa tournée, d'après l'heure actuelle
  // et les horaires prévus des points (position théorique, hors aléas réels)
  const estimateTourPosition = (tour, mode = routingMode) => {
    const points = (tour.itinerary || []).filter((point) =>
      point.spot_coordinates && point.spot_coordinates.lat && point.delivery_time
    );
    if (points.length === 0) {
      return;
    }

    // Affiche la tournée sur la carte si elle ne l'est pas
    if (!selectedTourIds.includes(tour.id)) {
      setSelectedTourIds((prev) => [...prev, tour.id]);
    }

    const now = moment();
    const toMoment = (time) => moment(`${date} ${moment(time).format("HH:mm")}`, "YYYY-MM-DD HH:mm");
    const coords = (point) => [point.spot_coordinates.lat, point.spot_coordinates.lng];

    let position = null;

    const first = points[0];
    const last = points[points.length - 1];
    const lastEnd = toMoment(last.departure_time || last.delivery_time);

    if (now.isBefore(toMoment(first.delivery_time))) {
      const depot = getDepotForTour(tour);
      const tourStart = tour.start_at ? toMoment(tour.start_at) : null;
      const firstArrival = toMoment(first.delivery_time);

      if (depot && tourStart && now.isBefore(tourStart)) {
        // Pas encore parti : au dépôt de l'établissement
        position = {
          lat: depot.lat,
          lng: depot.lng,
          label: `${tour.name} — au dépôt ${depot.name} (départ prévu ${tourStart.format("HH:mm")})`,
        };
      } else if (depot) {
        // Entre le dépôt et le premier point : avancée sur le trajet selon le temps écoulé
        let fraction = 0.5;
        if (tourStart && firstArrival.diff(tourStart) > 0) {
          fraction = Math.min(1, Math.max(0, now.diff(tourStart) / firstArrival.diff(tourStart)));
        }
        position = {
          from: [depot.lat, depot.lng],
          to: coords(first),
          fraction: fraction,
          label: `${tour.name} — parti de ${depot.name}, en route vers ${first.spot_name} (arrivée prévue ${firstArrival.format("HH:mm")})`,
        };
      } else {
        position = {
          lat: coords(first)[0],
          lng: coords(first)[1],
          label: `${tour.name} — en route vers ${first.spot_name} (arrivée prévue ${firstArrival.format("HH:mm")})`,
        };
      }
    } else if (now.isAfter(lastEnd)) {
      position = {
        lat: coords(last)[0],
        lng: coords(last)[1],
        label: `${tour.name} — tournée normalement terminée (${last.spot_name} quitté à ${lastEnd.format("HH:mm")})`,
      };
    } else {
      for (let i = 0; i < points.length && !position; i++) {
        const point = points[i];
        const arrival = toMoment(point.delivery_time);
        const departure = toMoment(point.departure_time || point.delivery_time);

        // Au point (entre arrivée et départ prévus)
        if (now.isSameOrAfter(arrival) && now.isSameOrBefore(departure)) {
          position = {
            lat: coords(point)[0],
            lng: coords(point)[1],
            label: `${tour.name} — au point ${point.spot_name} (départ prévu ${departure.format("HH:mm")})`,
          };
        }
        // Entre ce point et le suivant : avancée sur le trajet selon le temps écoulé
        else if (points[i + 1]) {
          const next = points[i + 1];
          const nextArrival = toMoment(next.delivery_time);
          if (now.isAfter(departure) && now.isBefore(nextArrival)) {
            const total = nextArrival.diff(departure);
            const fraction = total > 0 ? now.diff(departure) / total : 0.5;
            position = {
              from: coords(point),
              to: coords(next),
              fraction: fraction,
              label: `${tour.name} — entre ${point.spot_name} et ${next.spot_name} (arrivée prévue ${nextArrival.format("HH:mm")})`,
            };
          }
        }
      }
    }

    if (position) {
      showEstimatedPosition(position, tour, mode);
    }
  };

  // Bascule vélo/voiture : les tracés se redessinent seuls (TourMap réagit au mode),
  // mais les résultats déjà affichés doivent être recalculés dans le nouveau mode.
  // Le mode est passé explicitement, le state n'étant pas encore à jour ici.
  const changeRoutingMode = (mode) => {
    if (mode === routingMode) {
      return;
    }
    setRoutingMode(mode);

    if (bikeTarget) {
      estimateTravelTime(bikeTarget, mode);
    }
    if (estimatedPosition) {
      const tour = tours.find((item) => item.id === estimatedPosition.tourId);
      if (tour) {
        estimateTourPosition(tour, mode);
      }
    }
  };

  // Intendance du jour (une par établissement et/ou une globale)
  useEffect(() => {
    dispatch(savOperations.getIntendanceStaff(date))
      .then((result) => {
        setStaff(result?.data?.result || []);
      })
      .catch(() => {
        setStaff([]);
      });
  }, [date, dispatch]);

  // Sélecteur de personne : mêmes options que « Livreur du jour » (pool DeliveryMan)
  const renderStaffSelect = (assignment, onChange) => (
    <Select
      options={deliverymen.map((deliveryMan) => ({
        value: deliveryMan.id,
        label: deliveryMan.firstname,
      }))}
      value={assignment
        ? { value: assignment.delivery_man.id, label: assignment.delivery_man.firstname }
        : null}
      onChange={(option) => onChange(option ? option.value : null)}
      placeholder="Non affectée"
      isClearable
      isSearchable
      noOptionsMessage={() => "Aucune personne trouvée"}
      menuPortalTarget={document.body}
      styles={{
        menuPortal: (base) => ({ ...base, zIndex: 2000 }),
        control: (base) => ({ ...base, minHeight: 31 }),
        dropdownIndicator: (base) => ({ ...base, padding: 4 }),
        clearIndicator: (base) => ({ ...base, padding: 4 }),
      }}
    />
  );

  // establishmentId null = intendance globale
  const findStaff = (establishmentId) => staff.find((item) => (
    establishmentId === null
      ? item.establishment === null
      : item.establishment && item.establishment.id === establishmentId
  ));

  const assignStaff = (establishmentId, deliveryManId) => {
    dispatch(savOperations.setIntendanceStaff(date, establishmentId, deliveryManId || null))
      .then((result) => {
        const assignment = result?.data?.result;
        setStaff((prev) => {
          const others = prev.filter((item) => (
            establishmentId === null
              ? item.establishment !== null
              : !(item.establishment && item.establishment.id === establishmentId)
          ));
          return assignment ? [...others, assignment] : others;
        });
      })
      .catch(() => {
      });
  };

  // Liste des livreurs (système livreur existant) pour l'affectation du jour
  useEffect(() => {
    dispatch(savOperations.getIntendanceDeliverymen())
      .then((result) => {
        setDeliverymen(result?.data?.result || []);
      })
      .catch(() => {
        setDeliverymen([]);
      });
  }, [dispatch]);

  // Rafraîchit les coches « livré » toutes les 15s : les livreurs cochent depuis
  // l'app livraison, on veut le voir sans recharger la page. Endpoint minimal
  // (juste les ids), fusionné dans le state sans toucher au reste de l'UI.
  // Pas de polling quand l'onglet est masqué (document.hidden).
  useEffect(() => {
    const refreshDelivered = () => {
      if (document.hidden) {
        return;
      }
      dispatch(savOperations.getDeliveredSpots(date))
        .then((result) => {
          const ids = result?.data?.result?.spot_ids;
          if (!Array.isArray(ids)) {
            return;
          }
          const deliveredIds = new Set(ids);
          // Horodatage du marquage par point (delivered_at)
          const deliveredAtBySpot = {};
          (result?.data?.result?.deliveries || []).forEach((delivery) => {
            deliveredAtBySpot[delivery.spot_id] = delivery.delivered_at;
          });
          setTours((prev) => {
            // Ne recrée le state que si quelque chose a changé (évite les re-rendus à vide)
            let changed = false;
            const next = prev.map((tour) => {
              const itinerary = (tour.itinerary || []).map((point) => {
                const delivered = deliveredIds.has(point.spot_id);
                const deliveredAt = deliveredAtBySpot[point.spot_id] || null;
                if (delivered === !!point.delivered && deliveredAt === (point.delivered_at || null)) {
                  return point;
                }
                changed = true;
                return { ...point, delivered: delivered, delivered_at: deliveredAt };
              });
              return { ...tour, itinerary };
            });
            return changed ? next : prev;
          });
        })
        .catch(() => {
        });
    };
    const timer = setInterval(refreshDelivered, 15000);
    return () => clearInterval(timer);
  }, [date, dispatch]);

  // Marque/dé-marque un point comme livré pour le jour affiché.
  // Un spot peut apparaître dans plusieurs tournées : le flag est par point + jour,
  // on met donc à jour toutes ses occurrences (comme le renvoie l'API).
  const toggleDelivered = (point, tour) => {
    const delivered = !point.delivered;
    const previousDeliveredAt = point.delivered_at || null;
    // Mise à jour optimiste, annulée si l'API échoue
    const applyDelivered = (value, deliveredAt) => {
      setTours((prev) => prev.map((item) => ({
        ...item,
        itinerary: (item.itinerary || []).map((p) =>
          p.spot_id === point.spot_id ? { ...p, delivered: value, delivered_at: deliveredAt } : p
        ),
      })));
    };
    // Optimiste : heure locale en attendant l'horodatage serveur
    applyDelivered(delivered, delivered ? moment().format("YYYY-MM-DD HH:mm:ss") : null);
    dispatch(savOperations.setSpotDelivered(date, point.spot_id, tour.id, delivered))
      .then((result) => {
        const deliveredAt = result?.data?.result?.delivered_at;
        if (delivered && deliveredAt) {
          applyDelivered(true, deliveredAt);
        }
      })
      .catch(() => {
        applyDelivered(!delivered, previousDeliveredAt);
      });
  };

  // Affecte (ou retire) le livreur du jour sur une tournée
  const assignDeliveryman = (tour, deliveryManId) => {
    dispatch(savOperations.setIntendanceAssignment(date, tour.id, deliveryManId || null))
      .then((result) => {
        const assignment = result?.data?.result;
        setTours((prev) => prev.map((item) =>
          item.id === tour.id
            ? { ...item, assigned_delivery_man: assignment ? assignment.delivery_man : null }
            : item
        ));
      })
      .catch(() => {
      });
  };

  // Suggestions d'adresse en temps réel (debounce 300ms)
  useEffect(() => {
    if (addressQuery.trim().length < 3) {
      setAddressSuggestions([]);
      return;
    }
    // Ne relance pas la recherche quand le champ vient d'être rempli par une sélection
    if (addressMarker && addressQuery === addressMarker.label) {
      setAddressSuggestions([]);
      return;
    }
    const timer = setTimeout(() => {
      axios.get(GEOCODE_URL, {
        params: {
          q: addressQuery,
          lat: LYON_CENTER.lat,
          lon: LYON_CENTER.lon,
          limit: 5,
        },
      })
        .then((response) => {
          setAddressSuggestions(response.data?.features || []);
        })
        .catch(() => {
          setAddressSuggestions([]);
        });
    }, 300);
    return () => clearTimeout(timer);
  }, [addressQuery, addressMarker]);

  const selectAddress = (feature) => {
    const [lng, lat] = feature.geometry.coordinates;
    setAddressMarker({ lat, lng, label: feature.properties.label });
    setAddressQuery(feature.properties.label);
    setAddressSuggestions([]);
    setBikeEstimate(null);
  };

  const clearAddress = () => {
    setAddressMarker(null);
    setAddressQuery("");
    setAddressSuggestions([]);
    setBikeEstimate(null);
  };

  // Estime le temps de trajet entre la position localisée (livreur) et un point,
  // dans le mode courant. `mode` est passé explicitement par le toggle, qui
  // recalcule avant que le state du mode ne soit à jour.
  const estimateTravelTime = (point, mode = routingMode) => {
    if (!addressMarker || !point.spot_coordinates || !point.spot_coordinates.lat) {
      return;
    }
    setBikeTarget(point);
    setBikeEstimate({ spotId: point.spot_id, spotName: point.spot_name, isLoading: true });

    fetchRoute(
      [
        [addressMarker.lat, addressMarker.lng],
        [point.spot_coordinates.lat, point.spot_coordinates.lng],
      ],
      mode
    )
      .then((route) => {
        if (!route) {
          throw new Error("no route");
        }
        setBikeEstimate({
          spotId: point.spot_id,
          spotName: point.spot_name,
          minutes: route.minutes,
          km: route.km,
          latlngs: route.latlngs,
          deliveryTime: point.delivery_time || null,
          isLoading: false,
        });
      })
      .catch(() => {
        setBikeEstimate({
          spotId: point.spot_id,
          spotName: point.spot_name,
          error: true,
          isLoading: false,
        });
      });
  };

  const toggleTour = (tourId) => {
    setSelectedTourIds((prev) => {
      const next = prev.includes(tourId) ? prev.filter((id) => id !== tourId) : [...prev, tourId];
      // Retire de la sélection les points qui ne sont plus affichés
      const visibleSpotIds = tours
        .filter((tour) => next.includes(tour.id))
        .reduce((acc, tour) => acc.concat((tour.itinerary || []).map((point) => point.spot_id)), []);
      setSelectedSpots((spots) => spots.filter((spot) => visibleSpotIds.includes(spot.spot_id)));
      return next;
    });
  };

  const toggleSpot = (point, tour) => {
    setSelectedSpots((prev) => {
      if (prev.some((spot) => spot.spot_id === point.spot_id)) {
        return prev.filter((spot) => spot.spot_id !== point.spot_id);
      }
      return [...prev, {
        spot_id: point.spot_id,
        spot_name: point.spot_name,
        tourId: tour.id,
        tourName: tour.name,
        contactPhone: tour.contact_phone,
      }];
    });
  };

  const selectAllSpotsOfTour = (tour) => {
    setSelectedSpots((prev) => {
      const next = [...prev];
      (tour.itinerary || []).forEach((point) => {
        if (!next.some((spot) => spot.spot_id === point.spot_id)) {
          next.push({
            spot_id: point.spot_id,
            spot_name: point.spot_name,
            tourId: tour.id,
            tourName: tour.name,
            contactPhone: tour.contact_phone,
          });
        }
      });
      return next;
    });
  };

  return (
    <Container>
      <Row className="mt-3">

        {/* Colonne de gauche : date + filtres + tournées */}
        <Col md={4} lg={3}>
          <Sidebar>
            <Form.Group className="mb-2">
              <Form.Label className="mb-1"><strong>Date de la tournée</strong></Form.Label>
              <Form.Control
                type="date"
                value={date}
                onChange={(e) => e.target.value && setDate(e.target.value)}
              />
            </Form.Group>

            <Form.Group className="mb-2">
              <Form.Control
                as="select"
                value={establishmentFilter}
                onChange={(e) => setEstablishmentFilter(e.target.value)}
              >
                <option value="">Tous les établissements</option>
                {establishments.map((establishment) => (
                  <option key={establishment.id} value={String(establishment.id)}>
                    {establishment.name}
                  </option>
                ))}
              </Form.Control>
            </Form.Group>

            <Form.Group className="mb-2">
              <Form.Control
                type="search"
                placeholder="Filtrer par tournée ou point de retrait..."
                value={nameFilter}
                onChange={(e) => setNameFilter(e.target.value)}
              />
            </Form.Group>

            <Button
              variant="outline-dark"
              size="sm"
              block
              className="mb-2"
              disabled={tours.length === 0}
              onClick={() => setShowRecap(true)}
              data-tour="recap"
            >
              Attribuer livreur
            </Button>

            {isLoading ? (
              <Loader items={3} />
            ) : (
              <ToursList data-tour="tours-list">
                {tours.length === 0 && (
                  <p className="text-muted">Aucune tournée pour cette date.</p>
                )}
                {tours.length > 0 && filteredTours.length === 0 && (
                  <p className="text-muted">Aucune tournée ne correspond aux filtres.</p>
                )}
                {filteredTours.map((tour) => {
                const isTourSelected = selectedTourIds.includes(tour.id);
                return (
                  <TourCard key={tour.id} color={tour.displayColor} selected={isTourSelected}>
                    <div className="d-flex justify-content-between align-items-start">
                      <span>
                        <ColorDot color={tour.displayColor} />
                        <strong>{tour.name}</strong>
                        {tour.start_at && tour.end_at &&
                          <small className="text-muted d-block">
                            Départ {moment(tour.start_at).format("HH:mm")} — Retour {moment(tour.end_at).format("HH:mm")}
                          </small>
                        }
                      </span>
                      <Button
                        variant={isTourSelected ? "secondary" : "outline-secondary"}
                        size="sm"
                        className="py-0 ml-1 flex-shrink-0"
                        onClick={() => toggleTour(tour.id)}
                        data-tour="voir-pdr"
                      >
                        {isTourSelected ? "Cacher PDR" : "Voir PDR"}
                      </Button>
                    </div>

                    <div className="mt-1">
                      {/* Affectation en lecture seule : elle se fait dans « Attribuer livreur » */}
                      <small className="d-block mb-1">
                        {tour.assigned_delivery_man ? (
                          <>
                            <span role="img" aria-label="livreur">🚴</span>{" "}
                            {tour.assigned_delivery_man.firstname}
                            {tour.assigned_delivery_man.phone &&
                              <> — <a href={`tel:${tour.assigned_delivery_man.phone}`}>{tour.assigned_delivery_man.phone}</a></>
                            }
                          </>
                        ) : (
                          <span className="text-muted">Aucun livreur affecté</span>
                        )}
                      </small>
                      {date === moment().format("YYYY-MM-DD") &&
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          className="py-0 mr-1 mb-1"
                          title="Position théorique du livreur d'après les horaires de la tournée"
                          onClick={() => estimateTourPosition(tour)}
                        >
                          <span role="img" aria-label="position">📍</span> Position théorique
                        </Button>
                      }
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        className="py-0 mb-1"
                        data-tour="notes-tour"
                        onClick={() => setNotesTarget({
                          vehicleId: tour.id,
                          vehicleName: tour.name,
                          spots: (tour.itinerary || []).reduce((acc, point) => {
                            if (!acc.some((spot) => spot.id === point.spot_id)) {
                              acc.push({ id: point.spot_id, name: point.spot_name });
                            }
                            return acc;
                          }, []),
                        })}
                      >
                        <span role="img" aria-label="notes">🗒</span> Notes de la tournée
                      </Button>
                    </div>

                    {isTourSelected && (
                      <div className="mt-2">
                        <Button
                          variant="link"
                          size="sm"
                          className="p-0 mb-1"
                          onClick={() => selectAllSpotsOfTour(tour)}
                        >
                          Sélectionner tous les points
                        </Button>
                        {(tour.itinerary || []).map((point, index) => (
                          <PointRow
                            key={`${tour.id}-${point.schedule_id || point.spot_id}`}
                            id={`point-row-${tour.id}-${point.spot_id}`}
                            focused={!!focusSpot && focusSpot.spotId === point.spot_id}
                          >
                          <Form.Check
                            type="checkbox"
                            id={`spot-${tour.id}-${point.schedule_id || point.spot_id}`}
                            checked={selectedSpotIds.includes(point.spot_id)}
                            onChange={() => toggleSpot(point, tour)}
                            label={
                              <small>
                                <SpotNumber color={tour.displayColor} textColor={tour.color_text}>
                                  {point.spot_number || index + 1}
                                </SpotNumber>
                                {highlightedSpotIds.includes(point.spot_id)
                                  ? <HighlightedName>{point.spot_name}</HighlightedName>
                                  : point.spot_name}
                                {point.delivery_time &&
                                  <span className="text-muted">
                                    {" — "}{moment(point.delivery_time).format("HH:mm")}
                                    {point.departure_time && ` - ${moment(point.departure_time).format("HH:mm")}`}
                                  </span>
                                }
                                {(!point.spot_coordinates || !point.spot_coordinates.lat) &&
                                  <span className="text-danger"> (pas de coordonnées)</span>
                                }
                                {point.delivered && point.delivered_at &&
                                  <span className="text-success font-weight-bold">
                                    {" — Livré à "}{moment(point.delivered_at).format("HH:mm")}
                                  </span>
                                }
                              </small>
                            }
                          />
                          <Button
                            variant="link"
                            size="sm"
                            className="p-0 ml-1"
                            title="Notes de suivi du point"
                            onClick={() => setNotesTarget({
                              vehicleId: tour.id,
                              vehicleName: tour.name,
                              spotId: point.spot_id,
                              spotName: point.spot_name,
                            })}
                          >
                            <span role="img" aria-label="notes">🗒</span>
                          </Button>
                          <DeliveredToggle
                            type="button"
                            delivered={point.delivered}
                            title={point.delivered
                              ? `Livré${point.delivered_at ? ` à ${moment(point.delivered_at).format("HH:mm")}` : ""} — cliquer pour annuler`
                              : "Marquer ce point comme livré"}
                            onClick={() => toggleDelivered(point, tour)}
                          >
                            ✓
                          </DeliveredToggle>
                          </PointRow>
                        ))}
                      </div>
                    )}
                  </TourCard>
                );
                })}
              </ToursList>
            )}
          </Sidebar>
        </Col>

        {/* Colonne de droite : actions + carte */}
        <Col md={8} lg={9}>
          <ActionBar>
            <Button
              variant="primary"
              size="sm"
              className="mr-3"
              onClick={() => setShowGuide(true)}
            >
              <span role="img" aria-label="guide">💡</span> Tutoriel - Que voulez-vous faire ?
            </Button>

            {/* Mode de déplacement : s'applique aux tracés des tournées,
                à la position théorique du livreur et aux estimations de trajet */}
            <ButtonGroup size="sm" className="mr-3" data-tour="routing-mode">
              {Object.values(ROUTING_MODES).map((mode) => (
                <Button
                  key={mode.key}
                  variant={routingMode === mode.key ? "secondary" : "outline-secondary"}
                  onClick={() => changeRoutingMode(mode.key)}
                  title={`Calculer les itinéraires et estimations en ${mode.label.toLowerCase()}`}
                >
                  <span role="img" aria-label={mode.label}>{mode.emoji}</span> {mode.label}
                </Button>
              ))}
            </ButtonGroup>
            <span className="mr-3">
              <strong>{selectedSpots.length}</strong> point(s) sélectionné(s)
              {selectedSpots.length > 0 &&
                <Button variant="link" size="sm" onClick={() => setSelectedSpots([])}>
                  Tout désélectionner
                </Button>
              }
            </span>
            <ButtonGroup>
              <Button
                variant="outline-primary"
                onClick={() => setAction("sms")}
                data-tour="sms"
              >
                Envoyer un SMS
              </Button>
              <Button
                variant="outline-primary"
                onClick={() => setAction("email")}
              >
                Envoyer un email
              </Button>
            </ButtonGroup>
          </ActionBar>

          <Row className="mt-2" noGutters>
            <Col md={6} className="pr-md-1">
              <AddressSearch data-tour="address-search">
                <div style={{ position: "relative", flex: "1 1 auto" }}>
                  <Form.Control
                    type="search"
                    placeholder="Localiser un livreur en tapant une adresse"
                    value={addressQuery}
                    onChange={(e) => {
                      setAddressQuery(e.target.value);
                      if (!e.target.value) {
                        clearAddress();
                      }
                    }}
                    onKeyDown={(e) => {
                      // Entrée = valide la première suggestion (point du jour en priorité)
                      if (e.key === "Enter") {
                        if (addressSpotSuggestions.length > 0) {
                          e.preventDefault();
                          selectSpotAsAddress(addressSpotSuggestions[0]);
                        } else if (addressSuggestions.length > 0) {
                          e.preventDefault();
                          selectAddress(addressSuggestions[0]);
                        }
                      }
                    }}
                    onFocus={() => setAddressFocused(true)}
                    onBlur={() => setTimeout(() => {
                      setAddressSuggestions([]);
                      setAddressFocused(false);
                    }, 200)}
                  />
                  {addressFocused && (addressSpotSuggestions.length > 0 || addressSuggestions.length > 0) && (
                    <SuggestionsList>
                      {addressSpotSuggestions.map((item) => (
                        <ListGroup.Item
                          action
                          key={`spot-${item.point.spot_id}`}
                          onMouseDown={() => selectSpotAsAddress(item)}
                        >
                          <SpotNumber color={item.tour.displayColor} textColor={item.tour.color_text}>
                            {item.point.spot_number || "?"}
                          </SpotNumber>
                          <strong>{item.point.spot_name}</strong>
                          <small className="text-muted"> — point de la tournée {item.tour.name}</small>
                        </ListGroup.Item>
                      ))}
                      {addressSuggestions.map((feature) => (
                        <ListGroup.Item
                          action
                          key={feature.properties.id || feature.properties.label}
                          onMouseDown={() => selectAddress(feature)}
                        >
                          {feature.properties.label}
                        </ListGroup.Item>
                      ))}
                    </SuggestionsList>
                  )}
                </div>
                {addressMarker && (
                  <Button variant="outline-secondary" className="ml-2" onClick={clearAddress}>
                    Effacer <span role="img" aria-label="localisation livreur">{currentMode.emoji}</span>
                  </Button>
                )}
              </AddressSearch>
            </Col>
            <Col md={6} className="pl-md-1 mt-2 mt-md-0">
              <div style={{ position: "relative" }} data-tour="customer-search">
                <Form.Control
                  type="search"
                  placeholder="Rechercher un client (nom, prénom, téléphone)..."
                  value={customerQuery}
                  onChange={(e) => setCustomerQuery(e.target.value)}
                  onKeyDown={(e) => {
                    // Entrée = valide le premier client suggéré
                    if (e.key === "Enter" && customerSuggestions.length > 0) {
                      e.preventDefault();
                      selectCustomer(customerSuggestions[0]);
                    }
                  }}
                  onBlur={() => setTimeout(() => setCustomerQuery(""), 200)}
                />
                {customerSuggestions.length > 0 && (
                  <SuggestionsList>
                    {customerSuggestions.map((customer) => (
                      <ListGroup.Item
                        action
                        key={customer.id}
                        onMouseDown={() => selectCustomer(customer)}
                      >
                        <strong>{customer.firstname} {customer.lastname}</strong>
                        {customer.phone && <> — {customer.phone}</>}
                        <small className="text-muted d-block">{customer.email}</small>
                      </ListGroup.Item>
                    ))}
                  </SuggestionsList>
                )}
              </div>
            </Col>
          </Row>

          {addressMarker && bikeEstimate && (
            <BikeCard data-tour="bike-banner">
              <span role="img" aria-label={currentMode.label}>{currentMode.emoji}</span>{" "}
              {bikeEstimate.isLoading && <>Calcul de l'itinéraire {currentMode.label.toLowerCase()} vers <strong>{bikeEstimate.spotName}</strong>...</>}
              {!bikeEstimate.isLoading && bikeEstimate.error &&
                <>Itinéraire {currentMode.label.toLowerCase()} indisponible vers <strong>{bikeEstimate.spotName}</strong>.</>
              }
              {!bikeEstimate.isLoading && !bikeEstimate.error &&
                <>
                  ≈ <strong>{bikeEstimate.minutes} min {currentMode.travelLabel}</strong>{" "}
                  ({bikeEstimate.km.toFixed(1)} km) jusqu'à <strong>{bikeEstimate.spotName}</strong>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    className="ml-2 py-0"
                    onClick={sendSmsForEstimate}
                  >
                    Envoyer un SMS
                  </Button>
                  {bikeEstimate.deliveryTime && date === moment().format("YYYY-MM-DD") && (() => {
                    // Arrivée estimée (maintenant + trajet) vs heure d'arrivée prévue au point
                    const target = moment(`${date} ${moment(bikeEstimate.deliveryTime).format("HH:mm")}`, "YYYY-MM-DD HH:mm");
                    const arrival = moment().add(bikeEstimate.minutes, "minutes");
                    const diff = target.diff(arrival, "minutes");
                    return diff >= 0 ? (
                      <Badge variant="success" className="ml-2">
                        Dans les temps — arrivée estimée {arrival.format("HH:mm")} pour {target.format("HH:mm")} ({diff} min de marge)
                      </Badge>
                    ) : (
                      <Badge variant="danger" className="ml-2">
                        En retard de {-diff} min — arrivée estimée {arrival.format("HH:mm")} pour {target.format("HH:mm")}
                      </Badge>
                    );
                  })()}
                </>
              }
              <Button variant="link" size="sm" className="p-0 ml-2" onClick={() => setBikeEstimate(null)}>
                ✕
              </Button>
            </BikeCard>
          )}

          {addressMarker && !bikeEstimate && (
            <BikeHint data-tour="bike-banner">
              <span role="img" aria-label={currentMode.label}>{currentMode.emoji}</span>{" "}
              <Button
                variant="outline-primary"
                size="sm"
                className="py-0"
                data-tour="bike-estimate"
                disabled={bikeTargetPoints.length === 0}
                onClick={() => setShowSpotPicker(true)}
              >
                Faire une estimation d'arrivée sur le point
              </Button>
            </BikeHint>
          )}

          {selectedCustomer && (
            <CustomerCard data-tour="customer-card">
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <strong>{selectedCustomer.firstname} {selectedCustomer.lastname}</strong>
                  {selectedCustomer.phone &&
                    <> — <a href={`tel:${selectedCustomer.phone}`}>{selectedCustomer.phone}</a></>
                  }
                  <small className="text-muted d-block">{selectedCustomer.email}</small>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    className="mt-1 py-0"
                    onClick={() => setDetailCustomerId(selectedCustomer.id)}
                  >
                    En savoir plus sur le client
                  </Button>
                </div>
                <Button variant="link" size="sm" className="p-0" onClick={clearCustomer}>
                  Fermer ✕
                </Button>
              </div>

              {isSearchingOrders ? (
                <small className="text-muted">Recherche des commandes du {moment(date).format("DD/MM/YYYY")}...</small>
              ) : (
                <>
                  {customerOrders.length === 0 &&
                    <div className="text-muted mt-1">
                      Aucune commande le {moment(date).format("DD/MM/YYYY")}.
                    </div>
                  }
                  {customerOrders.map((order) => {
                    const match = findOrderPoint(order);
                    const deliveryInfo = order.delivery_info || {};
                    return (
                      <div className="mt-1" key={order.id}>
                        <Badge variant="success" className="mr-1">Commande n°{order.number_order || order.id}</Badge>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          className="py-0 mr-2"
                          href={`${process.env.REACT_APP_LEMOULIN_BO_URL}/e28XmZh3/commande-production/${order.id}/show`}
                          target="_blank"
                          rel="noopener noreferrer"
                          title="Ouvrir la fiche de production de cette commande dans le BO"
                        >
                          <span role="img" aria-label="fiche">📋</span> Fiche prod
                        </Button>
                        {deliveryInfo.type === "shop" ? (
                          <>Retrait boutique — {deliveryInfo.address}</>
                        ) : (
                          <>
                            {match ? (
                              <>
                                <strong>{match.point.spot_name}</strong>
                                {" "}({deliveryInfo.number_spot}) — tournée {match.tour.name}
                                {match.point.delivery_time &&
                                  <> — {moment(match.point.delivery_time).format("HH:mm")}
                                    {match.point.departure_time && ` - ${moment(match.point.departure_time).format("HH:mm")}`}
                                  </>
                                }
                                {match.point.spot_coordinates && match.point.spot_coordinates.lat &&
                                  <Button
                                    variant="outline-primary"
                                    size="sm"
                                    className="ml-2 py-0"
                                    onClick={() => locateOrder(match)}
                                  >
                                    Voir sur la carte
                                  </Button>
                                }
                                <Button
                                  variant="outline-secondary"
                                  size="sm"
                                  className="ml-1 py-0"
                                  title="Annoter ce point"
                                  onClick={() => setNotesTarget({
                                    vehicleId: match.tour.id,
                                    vehicleName: match.tour.name,
                                    spotId: match.point.spot_id,
                                    spotName: match.point.spot_name,
                                  })}
                                >
                                  <span role="img" aria-label="notes">🗒</span> Annoter
                                </Button>
                              </>
                            ) : (
                              <>Point {deliveryInfo.number_spot || "?"}{deliveryInfo.vehicle_name && ` — tournée ${deliveryInfo.vehicle_name}`}</>
                            )}
                          </>
                        )}
                      </div>
                    );
                  })}
                </>
              )}
            </CustomerCard>
          )}

          <div className="mt-2" data-tour="map-area">
            <TourMap
              tours={selectedTours}
              selectedSpotIds={selectedSpotIds}
              highlightedSpotIds={focusSpot
                ? [...highlightedSpotIds, focusSpot.spotId]
                : highlightedSpotIds}
              onToggleSpot={toggleSpot}
              addressMarker={addressMarker}
              addressEstimate={addressEstimateText}
              centerOn={focusSpot}
              bikeRoute={bikeEstimate && bikeEstimate.latlngs ? bikeEstimate.latlngs : null}
              bikeTargetSpotId={bikeEstimate && !bikeEstimate.error ? bikeEstimate.spotId : null}
              estimatedPosition={estimatedPosition}
              routingMode={routingMode}
            />
          </div>
        </Col>

      </Row>

      <ActionModal
        action={action}
        date={date}
        selectedSpots={selectedSpots}
        availableSpots={availableSpots}
        onChangeSelectedSpots={setSelectedSpots}
        prefillContent={smsPrefill}
        onClose={() => {
          setAction(null);
          setSmsPrefill(null);
        }}
      />

      <NotesModal
        target={notesTarget}
        date={date}
        onClose={() => setNotesTarget(null)}
      />

      <CustomerDetailModal
        customerId={detailCustomerId}
        onClose={() => setDetailCustomerId(null)}
      />

      {/* Choix du point de retrait cible pour l'estimation vélo :
          permet de viser un point sans avoir à déplier sa tournée */}
      <Modal show={showSpotPicker} onHide={closeSpotPicker} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            Estimer le temps {currentMode.travelLabel} jusqu'à...
            {recapEstablishmentName && <> — {recapEstablishmentName}</>}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted">
            Depuis <strong>{addressMarker ? addressMarker.label : ""}</strong>
          </p>
          <Form.Control
            type="search"
            className="mb-2"
            placeholder="Filtrer par point de retrait ou tournée..."
            value={spotPickerFilter}
            onChange={(e) => setSpotPickerFilter(e.target.value)}
          />
          {filteredBikeTargetPoints.length === 0 ? (
            <p className="text-muted mb-0">Aucun point de retrait ne correspond.</p>
          ) : (
            <SpotPickerList>
              {filteredBikeTargetPoints.map((item) => (
                <ListGroup.Item
                  action
                  key={item.point.spot_id}
                  onClick={() => chooseBikeTarget(item)}
                >
                  <SpotNumber color={item.tour.displayColor} textColor={item.tour.color_text}>
                    {item.point.spot_number || "?"}
                  </SpotNumber>
                  <strong>{item.point.spot_name}</strong>
                  <small className="text-muted d-block">
                    Tournée {item.tour.name}
                    {item.point.delivery_time &&
                      <> — {moment(item.point.delivery_time).format("HH:mm")}
                        {item.point.departure_time && ` - ${moment(item.point.departure_time).format("HH:mm")}`}
                      </>
                    }
                  </small>
                </ListGroup.Item>
              ))}
            </SpotPickerList>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={closeSpotPicker}>
            Annuler
          </Button>
        </Modal.Footer>
      </Modal>

      <GuideModal
        show={showGuide}
        onClose={() => setShowGuide(false)}
        onStartTutorial={(guide) => {
          setShowGuide(false);
          setTutorial({ guide, stepIndex: 0 });
        }}
      />

      {tutorial && (
        <TutorialOverlay
          guide={tutorial.guide}
          stepIndex={tutorial.stepIndex}
          onPrev={() => setTutorial((prev) => ({ ...prev, stepIndex: Math.max(0, prev.stepIndex - 1) }))}
          onNext={() => setTutorial((prev) => ({ ...prev, stepIndex: prev.stepIndex + 1 }))}
          onClose={() => setTutorial(null)}
        />
      )}

      {/* Récap de l'attribution des livreurs aux tournées */}
      <Modal show={showRecap} onHide={() => setShowRecap(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            Récap du {moment(date).format("DD/MM/YYYY")}
            {recapEstablishmentName && <> — {recapEstablishmentName}</>}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <h6 className="mb-2">
            <span role="img" aria-label="intendance">🏠</span> Intendance du jour
          </h6>
          <Table striped size="sm" className="mb-4">
            <thead>
              <tr>
                <th>Périmètre</th>
                <th>Personne</th>
                <th>Téléphone</th>
              </tr>
            </thead>
            <tbody>
              {[{ id: null, name: "Tous les établissements" }, ...recapEstablishments].map((establishment) => {
                const assignment = findStaff(establishment.id);
                return (
                  <tr key={establishment.id === null ? "global" : establishment.id}>
                    <td>
                      <strong>{establishment.name}</strong>
                      {establishment.id === null &&
                        <small className="text-muted d-block">Couvre les deux établissements</small>
                      }
                    </td>
                    <td style={{ minWidth: "220px" }}>
                      {renderStaffSelect(assignment, (deliveryManId) =>
                        assignStaff(establishment.id, deliveryManId))}
                    </td>
                    <td>
                      {assignment && assignment.delivery_man.phone
                        ? <a href={`tel:${assignment.delivery_man.phone}`}>{assignment.delivery_man.phone}</a>
                        : "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>

          <h6 className="mb-2">
            <span role="img" aria-label="charrette">🚴</span> Livreurs par charrette
          </h6>
          <Table striped size="sm">
            <thead>
              <tr>
                <th>Tournée</th>
                <th>Départ</th>
                <th>Établissement</th>
                <th>Livreur</th>
                <th>Téléphone</th>
              </tr>
            </thead>
            <tbody>
              {[...recapTours]
                .sort((a, b) => {
                  const estA = a.establishment ? a.establishment.name : "";
                  const estB = b.establishment ? b.establishment.name : "";
                  return estA.localeCompare(estB) || (a.name || "").localeCompare(b.name || "");
                })
                .map((tour) => (
                  <tr key={tour.id}>
                    <td>
                      <ColorDot color={tour.displayColor} />
                      <strong>{tour.name}</strong>
                    </td>
                    <td className="text-nowrap">
                      {tour.start_at ? moment(tour.start_at).format("HH:mm") : "—"}
                    </td>
                    <td>{tour.establishment ? tour.establishment.name : "—"}</td>
                    <td style={{ minWidth: "220px" }}>
                      <Select
                        options={deliverymen.map((deliveryMan) => ({
                          value: deliveryMan.id,
                          label: deliveryMan.firstname,
                        }))}
                        value={tour.assigned_delivery_man
                          ? { value: tour.assigned_delivery_man.id, label: tour.assigned_delivery_man.firstname }
                          : null}
                        onChange={(option) => assignDeliveryman(tour, option ? option.value : null)}
                        placeholder="Non affecté"
                        isClearable
                        isSearchable
                        noOptionsMessage={() => "Aucun livreur trouvé"}
                        menuPortalTarget={document.body}
                        styles={{
                          menuPortal: (base) => ({ ...base, zIndex: 2000 }),
                          control: (base) => ({ ...base, minHeight: 31 }),
                          dropdownIndicator: (base) => ({ ...base, padding: 4 }),
                          clearIndicator: (base) => ({ ...base, padding: 4 }),
                        }}
                      />
                    </td>
                    <td>
                      {tour.assigned_delivery_man && tour.assigned_delivery_man.phone
                        ? <a href={`tel:${tour.assigned_delivery_man.phone}`}>{tour.assigned_delivery_man.phone}</a>
                        : "—"}
                    </td>
                  </tr>
                ))}
            </tbody>
          </Table>
        </Modal.Body>
        <Modal.Footer>
          {recapFeedback && (
            <span
              className={`mr-auto font-weight-bold ${recapFeedback.variant === "success" ? "text-success" : "text-danger"}`}
            >
              {recapFeedback.variant === "success"
                ? <span role="img" aria-label="succès">✅</span>
                : <span role="img" aria-label="erreur">❌</span>}{" "}
              {recapFeedback.text}
            </span>
          )}
          <Button variant="outline-secondary" onClick={() => setShowRecap(false)}>
            Fermer
          </Button>
          <Button
            variant="primary"
            disabled={isSendingRecap || recapTours.length === 0}
            onClick={sendRecapToConnecteam}
          >
            {isSendingRecap ? (
              <>
                <Spinner animation="border" size="sm" className="mr-2" />
                Envoi en cours...
              </>
            ) : (
              "Envoyer sur Connecteam"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

/*
 * Thème local type "nuxt-ui" : palette grise neutre, accent émeraude,
 * arrondis doux, focus rings subtils. Scopé à l'onglet Intendance.
 */
const UI = {
  border: "#e5e7eb",
  borderStrong: "#d1d5db",
  bg: "#ffffff",
  bgSubtle: "#f9fafb",
  text: "#111827",
  muted: "#6b7280",
  primary: "#059669",
  primarySoft: "#ecfdf5",
  primaryBorder: "#a7f3d0",
  ring: "0 0 0 3px rgba(16, 185, 129, 0.18)",
  radius: "10px",
  radiusSm: "8px",
  shadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
  shadowLg: "0 10px 25px rgba(0, 0, 0, 0.08)",
};

const Container = styled.div`
  padding-bottom: 2rem;
  color: ${UI.text};
  font-size: 0.9rem;

  label,
  .form-check-label {
    font-size: 0.875rem;
  }

  .text-muted,
  small {
    color: ${UI.muted} !important;
  }

  .form-control {
    border-radius: ${UI.radiusSm};
    border-color: ${UI.border};
    font-size: 0.875rem;
    color: ${UI.text};
    transition: border-color 0.15s ease, box-shadow 0.15s ease;

    &::placeholder {
      color: #9ca3af;
    }

    &:focus {
      border-color: ${UI.primaryBorder};
      box-shadow: ${UI.ring};
    }
  }

  .btn {
    border-radius: ${UI.radiusSm};
    font-size: 0.875rem;
    font-weight: 500;
    transition: background 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease;

    &:focus {
      box-shadow: ${UI.ring};
    }
  }

  .btn-primary {
    color: #ffffff;
    background: ${UI.primary};
    border-color: ${UI.primary};

    &:hover:not(:disabled) {
      background: #047857;
      border-color: #047857;
    }
  }

  .btn-outline-primary {
    color: ${UI.primary};
    border-color: ${UI.primaryBorder};
    background: ${UI.bg};

    &:hover:not(:disabled) {
      color: ${UI.primary};
      background: ${UI.primarySoft};
      border-color: ${UI.primaryBorder};
    }
  }

  .btn-outline-secondary,
  .btn-outline-dark {
    color: #374151;
    border-color: ${UI.border};
    background: ${UI.bg};

    &:hover:not(:disabled) {
      color: ${UI.text};
      background: ${UI.bgSubtle};
      border-color: ${UI.borderStrong};
    }
  }

  .btn-secondary {
    color: #ffffff;
    background: ${UI.text};
    border-color: ${UI.text};

    &:hover:not(:disabled) {
      background: #374151;
      border-color: #374151;
    }
  }

  .btn-outline-success {
    color: ${UI.primary};
    border-color: ${UI.primaryBorder};
  }

  .btn-link {
    color: ${UI.primary};
    font-weight: 500;
  }

  .badge {
    border-radius: 9999px;
    font-weight: 600;
    padding: 0.35em 0.7em;
  }

  .alert {
    border-radius: ${UI.radius};
    font-size: 0.875rem;
  }
`;

// Panneau latéral aligné sur la hauteur de la carte (600px) : la liste scrolle au-delà
const Sidebar = styled.div`
  display: flex;
  flex-direction: column;
  max-height: 600px;
  background: ${UI.bg};
  border: 1px solid ${UI.border};
  border-radius: 12px;
  box-shadow: ${UI.shadow};
  padding: 0.75rem;
`;

const ToursList = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding-right: 4px;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: ${UI.border};
    border-radius: 3px;

    &:hover {
      background: ${UI.borderStrong};
    }
  }
`;

const TourCard = styled.div`
  border: 1px solid ${(props) => (props.selected ? props.color : UI.border)};
  border-left: 4px solid ${(props) => props.color};
  border-radius: ${UI.radius};
  padding: 0.65rem 0.75rem;
  margin-bottom: 0.5rem;
  background: ${UI.bg};
  transition: box-shadow 0.15s ease, border-color 0.15s ease;

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  }
`;

const ColorDot = styled.span`
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: ${(props) => props.color};
  margin-right: 6px;
  box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.08);
`;

const SpotNumber = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 7px;
  border-radius: 9999px;
  white-space: nowrap;
  background: ${(props) => props.color};
  color: ${(props) => props.textColor || "#fff"};
  font-weight: 600;
  font-size: 10px;
  margin-right: 6px;
  box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.08);
`;

const PointRow = styled.div`
  display: flex;
  align-items: flex-start;
  border-radius: 6px;
  padding: 1px 4px;
  background: ${(props) => (props.focused ? "#fef3c7" : "transparent")};
  transition: background 0.3s;

  &:hover {
    background: ${(props) => (props.focused ? "#fef3c7" : UI.bgSubtle)};
  }

  .form-check {
    flex: 1 1 auto;
  }
`;

// Coche « livré » d'un point : verte pleine quand livré, grise sinon
const DeliveredToggle = styled.button`
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  margin-left: 4px;
  margin-top: 1px;
  padding: 0;
  border-radius: 50%;
  border: 1px solid ${(props) => (props.delivered ? "#059669" : UI.borderStrong)};
  background: ${(props) => (props.delivered ? "#059669" : UI.bg)};
  color: ${(props) => (props.delivered ? "#ffffff" : UI.borderStrong)};
  font-size: 11px;
  line-height: 1;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;

  &:hover {
    border-color: #059669;
    color: ${(props) => (props.delivered ? "#ffffff" : "#059669")};
  }
`;

const HighlightedName = styled.mark`
  background: #fde68a;
  padding: 0 3px;
  border-radius: 4px;
  font-weight: 600;
`;

const ActionBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  background: ${UI.bg};
  border: 1px solid ${UI.border};
  border-radius: 12px;
  box-shadow: ${UI.shadow};
  padding: 0.5rem 0.75rem;
`;

const AddressSearch = styled.div`
  display: flex;
  align-items: flex-start;
`;

const BikeHint = styled.div`
  margin-top: 0.5rem;
  padding: 0.5rem 0.75rem;
  border: 1px solid ${UI.primaryBorder};
  border-radius: ${UI.radius};
  background: ${UI.primarySoft};
  color: #065f46;
  font-weight: 500;
  font-size: 0.875rem;
`;

const BikeCard = styled.div`
  border: 1px solid ${UI.border};
  border-left: 4px solid #06b6d4;
  border-radius: ${UI.radius};
  padding: 0.45rem 0.75rem;
  margin-top: 0.5rem;
  background: ${UI.bg};
  box-shadow: ${UI.shadow};
`;

const CustomerCard = styled.div`
  border: 1px solid ${UI.border};
  border-left: 4px solid ${UI.primary};
  border-radius: ${UI.radius};
  padding: 0.6rem 0.75rem;
  margin-top: 0.5rem;
  background: ${UI.bg};
  box-shadow: ${UI.shadow};
`;

// Liste des points de retrait dans la popup d'estimation : scrolle au-delà de ~8 points
const SpotPickerList = styled(ListGroup)`
  max-height: 45vh;
  overflow-y: auto;

  .list-group-item {
    cursor: pointer;
    padding: 0.5rem 0.75rem;

    &:hover {
      background: ${UI.bgSubtle};
    }
  }
`;

const SuggestionsList = styled(ListGroup)`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  z-index: 1100;
  border-radius: ${UI.radius};
  overflow: hidden;
  box-shadow: ${UI.shadowLg};

  .list-group-item {
    font-size: 0.875rem;
    border-color: ${UI.bgSubtle};
    padding: 0.5rem 0.75rem;

    &:hover {
      background: ${UI.bgSubtle};
    }
  }
`;

Intendance.propTypes = {};

Intendance.defaultProps = {};

export default Intendance;
